
import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setIsAuthenticated(true);
        setUserEmail(session.user.email || '');
        // Giriş yapıldığında ana sayfaya yönlendir
        if (event === 'SIGNED_IN') {
          navigate('/');
        }
      } else {
        setIsAuthenticated(false);
        setUserEmail('');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setIsAuthenticated(true);
      setUserEmail(session.user.email || '');
      // Zaten giriş yapılmışsa ana sayfaya yönlendir
      navigate('/');
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      
      // Giriş başarılı mesajı göster ve ana sayfaya yönlendir
      setError('Giriş başarılı! Ana sayfaya yönlendiriliyorsunuz...');
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error: any) {
      setError(error.message || 'Giriş yapılırken bir hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Önce kullanıcının zaten kayıtlı olup olmadığını kontrol et
      const { data: existingUser } = await supabase.auth.signInWithPassword({
        email,
        password: 'dummy-password' // Geçici şifre ile kontrol
      });

      // Eğer kullanıcı zaten varsa hata mesajı göster
      if (existingUser?.user) {
        setError('Bu e-posta adresi zaten kayıtlı. Giriş yapmayı deneyin.');
        return;
      }

      const { error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        // Eğer kullanıcı zaten kayıtlıysa özel mesaj göster
        if (error.message.includes('already registered') || error.message.includes('User already registered')) {
          setError('Bu e-posta adresi zaten kayıtlı. Giriş yapmayı deneyin.');
        } else {
          throw error;
        }
      } else {
        setError('Kayıt başarılı! E-posta adresinizi kontrol edin ve doğrulama linkine tıklayın.');
      }
    } catch (error: any) {
      if (error.message.includes('Invalid login credentials')) {
        // Bu durumda kullanıcı kayıtlı değil, normal kayıt işlemini yap
        try {
          const { error: signUpError } = await supabase.auth.signUp({
            email,
            password,
          });

          if (signUpError) {
            if (signUpError.message.includes('already registered') || signUpError.message.includes('User already registered')) {
              setError('Bu e-posta adresi zaten kayıtlı. Giriş yapmayı deneyin.');
            } else {
              throw signUpError;
            }
          } else {
            setError('Kayıt başarılı! E-posta adresinizi kontrol edin ve doğrulama linkine tıklayın.');
          }
        } catch (finalError: any) {
          setError(finalError.message || 'Kayıt olurken bir hata oluştu');
        }
      } else {
        setError(error.message || 'Kayıt olurken bir hata oluştu');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    setUserEmail('');
  };

  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-20 pb-16">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-green-100 rounded-full">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Hoş Geldiniz!
              </h1>
              <p className="text-gray-600 mb-2">
                Giriş yapan kullanıcı: <span className="font-medium text-gray-900">{userEmail}</span>
              </p>
              <p className="text-gray-600 mb-8">
                Artık favorilerinizi kaydedebilir ve sipariş takibi yapabilirsiniz.
              </p>
              <div className="space-y-4">
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Ana Sayfaya Git
                </button>
                <button
                  onClick={() => navigate('/gallery')}
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Galeriyi Keşfet
                </button>
                <button
                  onClick={() => navigate('/favorites')}
                  className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Favorilerim
                </button>
                <button
                  onClick={handleSignOut}
                  className="w-full text-gray-500 hover:text-gray-700 py-2 cursor-pointer"
                >
                  Çıkış Yap
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-20 pb-16">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-blue-100 rounded-full">
                <i className="ri-user-line text-3xl text-blue-600"></i>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {isSignUp ? 'Hesap Oluştur' : 'Giriş Yap'}
              </h1>
              <p className="text-gray-600">
                {isSignUp ? 'Yeni hesap oluşturun' : 'Hesabınıza giriş yapın'}
              </p>
            </div>

            <form onSubmit={isSignUp ? handleSignUp : handleSignIn} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-posta
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Şifre
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  minLength={6}
                />
              </div>

              {error && (
                <div className={`px-4 py-3 rounded-lg text-sm ${
                  error.includes('başarılı') 
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'
                }`}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                {isLoading ? 'Yükleniyor...' : (isSignUp ? 'Hesap Oluştur' : 'Giriş Yap')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setIsSignUp(!isSignUp);
                  setError('');
                  setEmail('');
                  setPassword('');
                }}
                className="text-blue-600 hover:text-blue-700 text-sm cursor-pointer"
              >
                {isSignUp ? 'Zaten hesabınız var mı? Giriş yapın' : 'Hesabınız yok mu? Kayıt olun'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
